import Button from "@edaz/edazkit/Button/index";
import AdvancedInput, { Transformation } from "@edaz/edazkit/controls/AdvancedInput";
import { IconType } from "@edaz/edazkit/Icon";
import { Input } from "@frui.ts/bootstrap";
import { registerView } from "@frui.ts/views";
import type { ViewComponent } from "@frui.ts/views/dist";
import { preventDefault } from "@frui.ts/views/dist";
import AccountNumberOrIbanAndBankCodeInput from "controls/finance/accountNumberOrIbanAndBankCodeInput";
import FormGroup from "controls/formGroup";
import LicensePlateInput from "controls/licensePlateInput";
import { observer } from "mobx-react-lite";
import React from "react";
import { Col } from "react-bootstrap";
import Form from "react-bootstrap/Form";
import { isFilterDirty, isFilterEmpty } from "utils/filters";
import SearchViewModel from "viewModels/callCenter/search/searchViewModel";
import CountrySelect from "../../common/countrySelect";
import EmailMessageViewModel from "viewModels/callCenter/search/emailMessage/emailMessageViewModel";
import DatePickerBox from "controls/datePickerBox";
import { disabledDaysRange } from "utils/dateUtils";
import { startOfEDAZEshop } from "config";

export const searchBarContext = "searchBar";

export const SearchBarView: ViewComponent<SearchViewModel> = observer(({ vm }) => {
  const guidRegex = new RegExp(/[^A-F0-9-]+/gi);
  const orderIdentRegex = new RegExp(/[^0-9]+/g);
  const emailMessageView = vm.activeChild instanceof EmailMessageViewModel;

  return (
    <div className="bg-white px-5 py-4 rounded h-100 search-bar">
      <Form onSubmit={preventDefault(vm.applyFilterAndLoad)} onReset={preventDefault(vm.resetFilterAndLoad)}>
        <Form.Row>
          <FormGroup as={Col} md={6} className="col-xxl-4" target={vm.filter} property="countryId">
            <Form.Label>{vm.translateAttribute("countryId")}</Form.Label>
            <CountrySelect
              allowEmpty
              vm={vm.countrySelectViewModel}
              placeholder={vm.translate("searchCountryId")}
              disabled={vm.disableInput("countryId")}
            />
          </FormGroup>
          <FormGroup as={Col} md={6} className="col-xxl-3" target={vm.filter} property="licensePlate">
            <Form.Label>{vm.translateAttribute("licensePlate")}</Form.Label>
            <LicensePlateInput placeholder={vm.translate("searchLicensePlate")} disabled={vm.disableInput("licensePlate")} />
          </FormGroup>
          <FormGroup as={Col} md={6} className="col-xxl-3" target={vm.filter} property="officialRecordId">
            <Form.Label>{vm.translateAttribute("officialRecordId")}</Form.Label>
            <AdvancedInput
              placeholder={vm.translate("searchRecordNumber")}
              transformation={Transformation.UpperCased}
              disabled={vm.disableInput("officialRecordId")}
            />
          </FormGroup>
          <FormGroup as={Col} md={6} className="col-xxl-2" target={vm.filter} property="orderIdent">
            <Form.Label>{vm.translateAttribute("orderIdent")}</Form.Label>
            <AdvancedInput
              placeholder={vm.translate("searchOrderIdent")}
              disabled={vm.disableInput("orderIdent")}
              ignoredCharacters={orderIdentRegex}
            />
          </FormGroup>
          {emailMessageView ? (
            <>
              <FormGroup as={Col} md={6} xl={4} target={vm.filter} property="_validSince">
                <Form.Label>{vm.translate("sentDate.from")}</Form.Label>
                <DatePickerBox
                  placeholder={vm.translate("search")}
                  fromMonth={startOfEDAZEshop}
                  toMonth={new Date()}
                  disabledDays={disabledDaysRange(new Date(), startOfEDAZEshop)}
                />
              </FormGroup>
              <FormGroup as={Col} md={6} xl={3} target={vm.filter} property="_validUntil">
                <Form.Label>{vm.translate("sentDate.to")}</Form.Label>
                <DatePickerBox
                  placeholder={vm.translate("search")}
                  fromMonth={startOfEDAZEshop}
                  toMonth={new Date()}
                  disabledDays={disabledDaysRange(new Date(), startOfEDAZEshop)}
                />
              </FormGroup>
            </>
          ) : (
            <>
              <FormGroup as={Col} md={6} xl={4} target={vm} property="filter">
                <Form.Label>{vm.translateAttribute("bankAccountNumber")}</Form.Label>
                <AccountNumberOrIbanAndBankCodeInput
                  placeholder={vm.translate("searchBankAccountNumber")}
                  disabled={vm.disableInput("bankAccountNumber")}
                  bankCodeProperty="bankCode"
                  accountNumberProperty="bankAccountNumber"
                />
              </FormGroup>
              <FormGroup as={Col} md={6} xl={3} target={vm.filter} property="cardNumber">
                <Form.Label>{vm.translateAttribute("cardNumber")}</Form.Label>
                <Input
                  placeholder={vm.translate("searchCardNumber")}
                  disabled={vm.disableInput("cardNumber")}
                  autoComplete="new-password"
                />
              </FormGroup>
            </>
          )}
          <FormGroup as={Col} md={6} xl={3} target={vm.filter} property="emailAddress">
            <Form.Label>{vm.translateAttribute("emailAddress")}</Form.Label>
            <Input placeholder={vm.translate("searchEmail")} disabled={vm.disableInput("emailAddress")} />
          </FormGroup>
          <FormGroup as={Col} md={6} xl={2} target={vm.filter} property="notificationPhoneNumber">
            <Form.Label>{vm.translateAttribute("notificationPhoneNumber")}</Form.Label>
            <Input placeholder={vm.translate("searchPhone")} disabled={vm.disableInput("notificationPhoneNumber")} />
          </FormGroup>
        </Form.Row>
        <Form.Row>
          <FormGroup as={Col} md={12} className="d-flex flex-column flex-xxl-row mb-0">
            <Form.Row className="flex-grow-1">
              <FormGroup as={Col} md={6} className="col-xxl-6" target={vm.filter} property="chargeOrderId">
                <Form.Label>{vm.translateAttribute("chargeOrderId")}</Form.Label>
                <AdvancedInput
                  placeholder={vm.translate("chargeOrderId")}
                  disabled={vm.disableInput("chargeOrderId")}
                  ignoredCharacters={guidRegex}
                />
              </FormGroup>
              <FormGroup as={Col} md={6} className="col-xxl-6" target={vm.filter} property="chargeId">
                <Form.Label>{vm.translateAttribute("chargeId")}</Form.Label>
                <AdvancedInput
                  placeholder={vm.translate("chargeId")}
                  disabled={vm.disableInput("chargeId")}
                  ignoredCharacters={guidRegex}
                />
              </FormGroup>
            </Form.Row>
            <Form.Row className="d-flex justify-content-end ml-xxl-1">
              <FormGroup as={Col} md={12}>
                <Form.Label className="d-xxl-inline-block d-none">&nbsp;</Form.Label>
                <div className="flex text-right text-nowrap">
                  {(isFilterDirty(vm.filter) || !isFilterEmpty(vm.filter)) && (
                    <Button iconLeft={IconType.Cross} variant="outline-danger" className="btn-form-height" type="reset">
                      {vm.translate("resetFilter")}
                    </Button>
                  )}
                  <Button iconLeft={IconType.Search} className="btn-form-height" type="submit" disabled={vm.busyWatcher?.isBusy}>
                    {vm.translate("search")}
                  </Button>
                </div>
              </FormGroup>
            </Form.Row>
          </FormGroup>
        </Form.Row>
      </Form>
    </div>
  );
});

registerView(SearchBarView, SearchViewModel, searchBarContext);
